import { Container, Graphics } from 'pixi.js';

import { project, type County, type Projection, type Router, type Vehicle } from '@hotlist/shared';

import type { Viewport } from './viewport.ts';

/**
 * Where you are going, and what it is costing you.
 *
 * Two lines, and the second one is the point.
 *
 * The first is the route the car is actually driving. Obvious, and it was
 * missing: a player clicked a junction and then watched a dot wander with no
 * indication of where it thought it was going.
 *
 * The second only appears when **avoid cameras** is on, and it is the route the
 * car *would* have taken. That comparison is the whole mechanic made visible.
 * Section 8 and the router are explicit that avoidance is a penalty and never a
 * ban -- roads you would rather not be seen on cost more, they are not
 * forbidden -- and the price is paid in distance. Without drawing it, a player
 * turns avoidance on, the route silently gets longer, and nothing tells them
 * what they bought. With it, the detour is the argument.
 *
 * ## What this does not do
 *
 * It does not draw the cameras being avoided, beyond the ones the player has
 * already found and which the camera layer already draws. The avoided route is
 * computed from **the client's own known set** -- the same edges it asked the
 * server to route around -- so the line can never reveal a camera nobody has
 * driven past. That is not a precaution bolted on: the client has no other
 * coverage to leak, because it was never sent any.
 */
export class RouteLayer {
  readonly container = new Container();

  private readonly taken = new Graphics();
  private readonly direct = new Graphics();
  /**
   * The way to a waypoint, when somebody is driving there themselves.
   *
   * Its own line because it is its own thing: `taken` is the route the car is
   * *being driven along* by the server, and this is a route nobody is
   * following -- it is drawn so a driver can see where to go while doing the
   * going.
   *
   * It was dashed and half-transparent at first, on the reasoning that a
   * suggestion should look quieter than an instruction. That is a nice idea and
   * a bad line: this is the only thing on screen telling somebody driving by
   * hand where to turn, and a navigation line you have to hunt for is not
   * navigation. Solid, bright, and its own color.
   */
  private readonly planned = new Graphics();
  private plannedFor = '';
  private plannedEdges: number[] = [];
  private readonly projection: Projection;
  private readonly county: County;

  /** What the direct route was last computed for, so it is not redone per frame. */
  private directFor = '';
  private directEdges: number[] = [];

  constructor(county: County, projection: Projection) {
    this.county = county;
    this.projection = projection;
    // The road not taken goes underneath, so where they overlap the line the
    // car is actually following is the one on top.
    this.container.addChild(this.direct, this.planned, this.taken);
  }

  /**
   * Redraw both lines.
   *
   * `avoid` is the set of edges this client asked the server to route around,
   * which is exactly what it discovered by driving. `router` is the shared one
   * the simulation uses, so the comparison route is computed the same way the
   * server computed the real one.
   */
  update(
    viewport: Viewport,
    self: Vehicle | null,
    router: Router,
    avoiding: boolean,
    avoid: ReadonlySet<number>,
    /** A junction somebody is navigating to by hand, or -1. */
    waypoint = -1,
  ): void {
    this.taken.clear();
    this.direct.clear();
    this.planned.clear();

    this.drawPlanned(viewport, self, router, waypoint);

    if (!self || self.destination < 0 || self.route.length === 0) {
      this.directFor = '';
      return;
    }

    const ahead = self.route.slice(Math.max(0, self.routeIndex));
    if (ahead.length === 0) return;

    // The comparison: what this trip would have been without the detour.
    // Recomputed only when the trip or the setting changes, because routing a
    // county is not a per-frame job.
    if (avoiding && avoid.size > 0) {
      const from = self.edge >= 0
        ? (self.forward
            ? (this.county.edgeB[self.edge] as number)
            : (this.county.edgeA[self.edge] as number))
        : self.atNode;
      const key = `${from}:${self.destination}`;
      if (key !== this.directFor) {
        this.directFor = key;
        this.directEdges = router.route(from, self.destination) ?? [];
      }
      // Only the parts that differ. Drawing the whole thing would put a red
      // line under the entire route and say nothing about what the detour is.
      const following = new Set(ahead);
      this.paint(
        this.direct,
        viewport,
        this.directEdges.filter((edge) => !following.has(edge)),
        0xd9584a,
        2,
        0.85,
      );
    } else {
      this.directFor = '';
      this.directEdges = [];
    }

    this.paint(this.taken, viewport, ahead, 0x4bd6a0, 2.5, 0.95);
  }

  private paint(
    into: Graphics,
    viewport: Viewport,
    edges: readonly number[],
    color: number,
    width: number,
    alpha: number,
  ): void {
    if (edges.length === 0) return;
    const visible = viewport.visibleBounds(200);

    for (const edge of edges) {
      if (edge < 0 || edge >= this.county.edgeCount) continue;
      const a = this.county.edgeA[edge] as number;
      const b = this.county.edgeB[edge] as number;

      const pa = project(this.projection, this.county.nodeX[a] as number, this.county.nodeY[a] as number);
      const pb = project(this.projection, this.county.nodeX[b] as number, this.county.nodeY[b] as number);

      // Cull whole segments that cannot be on screen. A cross-county route is
      // a few hundred edges and most of them are nowhere near the viewport.
      if (
        (pa.sx < visible.minX && pb.sx < visible.minX) ||
        (pa.sx > visible.maxX && pb.sx > visible.maxX) ||
        (pa.sy < visible.minY && pb.sy < visible.minY) ||
        (pa.sy > visible.maxY && pb.sy > visible.maxY)
      ) {
        continue;
      }

      into.moveTo(viewport.toScreenX(pa.sx), viewport.toScreenY(pa.sy));
      into.lineTo(viewport.toScreenX(pb.sx), viewport.toScreenY(pb.sy));
    }

    into.stroke({ color: color, width, alpha });
  }

  /**
   * The way to a waypoint, for somebody driving there themselves.
   *
   * Computed on this client, with the same router the server uses, because the
   * client already holds the county — and because nobody is being driven along
   * it, so there is nothing for the server to be authoritative about. It is a
   * line on a map, which is what a satnav is.
   *
   * Recomputed only when the trip changes. Routing a county is not free and
   * doing it sixty times a second to draw the same line is the sort of thing
   * that shows up as a warm laptop rather than as a bug.
   */
  private drawPlanned(
    viewport: Viewport,
    self: Vehicle | null,
    router: Router,
    waypoint: number,
  ): void {
    if (!self || waypoint < 0) {
      this.plannedFor = '';
      return;
    }

    /**
     * Where the route starts.
     *
     * The junction ahead when the car is moving, and the one it is parked at
     * when it is not. This used to give up entirely on a parked car, which is
     * the single most likely moment for somebody to be setting a waypoint --
     * you stop, you look at the map, you decide where to go.
     */
    const from =
      self.edge < 0
        ? self.atNode
        : self.forward
          ? (this.county.edgeB[self.edge] as number)
          : (this.county.edgeA[self.edge] as number);
    if (from < 0) {
      this.plannedFor = '';
      return;
    }
    const key = `${from}->${waypoint}`;
    if (key !== this.plannedFor) {
      this.plannedFor = key;
      this.plannedEdges = router.route(from, waypoint) ?? [];
    }
    if (this.plannedEdges.length === 0) return;

    for (let i = 0; i < this.plannedEdges.length; i++) {
      const edge = this.plannedEdges[i];
      if (edge === undefined) continue;
      const a = this.county.edgeA[edge] as number;
      const b = this.county.edgeB[edge] as number;
      const pa = project(this.projection, this.county.nodeX[a] as number, this.county.nodeY[a] as number);
      const pb = project(this.projection, this.county.nodeX[b] as number, this.county.nodeY[b] as number);
      this.planned.moveTo(viewport.toScreenX(pa.sx), viewport.toScreenY(pa.sy));
      this.planned.lineTo(viewport.toScreenX(pb.sx), viewport.toScreenY(pb.sy));
    }
    /**
     * Violet, which nothing else on this map is.
     *
     * Amber was the first choice and it is the color of a motorway here, so a
     * navigation line looked like a road -- the one confusion a navigation line
     * cannot afford. Green is the route the server is driving, white is
     * ordinary road, orange is arterial. Violet is left, and being unused is
     * the whole qualification.
     */
    this.planned.stroke({ width: 4, color: 0xc76bff, alpha: 0.95 });
  }
}
